import { VgApiResult, vgApiRequest } from "./vg-api.service";
import { ORDER_STATE } from "constants/bookingEnums";

/**
 * A booking is a service order: Org_ServiceOrder_Table, created through
 * Create_ServiceOrder. It ties a client (contact) to a booking configuration for
 * a slot of time, and the API creates the underlying appointment.
 *
 * The patient is attached as service-order metadata rather than a column,
 * because there is no link between a patient and a booking anywhere in the
 * schema - Org_ServiceOrder_Table and Org_Appointments_Table have no animalId,
 * and none of the existing production bookings reference a patient.
 *
 * Metadata is a first-class store here, not a dumping ground. Each row carries
 * a metaDataIdentifier and a metaDataGroupIdentifier alongside its value, so a
 * booking's patient is addressable by key rather than by position, and OlaCore
 * indexes (orgId, metaDataIdentifier, metaData) so looking one up is a seek.
 * Write the identifier and the group on every row - a value written without
 * them is the thing that makes metadata unqueryable, not metadata itself.
 *
 * The org read hydrates metadata in one set-based query for the whole page, so
 * a booking arrives with its patient already attached; readPatientBookings
 * narrows that result rather than issuing a request per booking.
 */
const PATIENT_METADATA_IDENTIFIER = "animalId";
const PATIENT_METADATA_GROUP = "patient";

/** Enum_MetaDataType.Custom. Like every Core enum here, it is 1-based. */
const METADATA_TYPE_CUSTOM = 4;

export type BookingInput = {
  orgId: number;
  contactId: number;
  serviceId: number;
  serviceFulfilmentConfigId: number;
  /** UTC ISO. See appointments.service for why these must be UTC. */
  start: string;
  end: string;
  /** Optional: a booking can be made for a client without naming the patient. */
  animalId?: number;
  monetaryAmount?: number;
  monetaryCurrency?: number;
};

export type Booking = {
  serviceOrderId: number;
  appointmentId?: number;
  contactId?: number;
  serviceId?: number;
  serviceFulfilmentConfigId?: number;
  start?: string;
  end?: string;
  orderState?: number;
  /** Derived from metadata, absent when the booking predates this feature. */
  animalId?: number;
};

function patientMetadata(animalId?: number) {
  if (!animalId || animalId <= 0) return [];
  return [
    {
      type: METADATA_TYPE_CUSTOM,
      metaData: String(animalId),
      metaDataIdentifier: PATIENT_METADATA_IDENTIFIER,
      metaDataGroupIdentifier: PATIENT_METADATA_GROUP,
    },
  ];
}

/** Pulls the patient back out of a booking's metadata, if it is there. */
function animalIdFromMetadata(raw: unknown): number | undefined {
  const list = (raw as Record<string, unknown>)?.metadataOptionList;
  if (!Array.isArray(list)) return undefined;
  for (const entry of list) {
    const m = entry as Record<string, unknown>;
    const identifier = String(m.metaDataIdentifier ?? m.MetaDataIdentifier ?? "");
    if (identifier === PATIENT_METADATA_IDENTIFIER) {
      const value = Number(m.metaData ?? m.MetaData);
      return Number.isFinite(value) && value > 0 ? value : undefined;
    }
  }
  return undefined;
}

function pickBooking(raw: unknown): Booking {
  const o = raw as Record<string, unknown>;
  const pick = (camel: string, pascal: string) => o[camel] ?? o[pascal];
  const num = (v: unknown) => (v != null ? Number(v) : undefined);
  return {
    serviceOrderId: Number(pick("serviceOrderId", "ServiceOrderId") ?? 0),
    appointmentId: num(pick("appointmentId", "AppointmentId")),
    contactId: num(pick("contactId", "ContactId")),
    serviceId: num(pick("serviceId", "ServiceId")),
    serviceFulfilmentConfigId: num(
      pick("serviceFulfilmentConfigId", "ServiceFulfilmentConfigId")
    ),
    start: pick("start", "Start") != null ? String(pick("start", "Start")) : undefined,
    end: pick("end", "End") != null ? String(pick("end", "End")) : undefined,
    orderState: num(pick("orderState", "OrderState")),
    animalId: animalIdFromMetadata(o),
  };
}

export async function createBooking(
  accessToken: string,
  payload: BookingInput
): Promise<VgApiResult<{ newRecordID?: number; NewRecordID?: number }>> {
  return vgApiRequest(accessToken, "Create_ServiceOrder", {
    orgId: payload.orgId,
    contactId: payload.contactId,
    serviceId: payload.serviceId,
    serviceFulfilmentConfigId: payload.serviceFulfilmentConfigId,
    start: payload.start,
    end: payload.end,
    // A booking made by clinic staff is confirmed - it is not a request awaiting
    // approval. PendingOrder is for the public booking flow, which does not exist yet.
    orderState: ORDER_STATE.CONFIRMED,
    monetaryAmount: payload.monetaryAmount ?? 0,
    monetaryCurrency: payload.monetaryCurrency ?? 978,
    metadataOptionList: patientMetadata(payload.animalId),
  });
}

type BookingsResponse = {
  serviceOrderList?: unknown[];
  ServiceOrderList?: unknown[];
};

export async function readOrgBookings(
  accessToken: string,
  orgId: number
): Promise<VgApiResult<Booking[]>> {
  const { data, error } = await vgApiRequest<BookingsResponse>(
    accessToken,
    "Read_All_Service_Orders_By_Org_ID",
    { orgId }
  );
  if (error) return { data: null, error };
  const list = data?.serviceOrderList ?? data?.ServiceOrderList ?? [];
  return { data: list.map(pickBooking), error: null };
}

/**
 * This patient's bookings, soonest first.
 *
 * Narrowed from the org read rather than fetched per patient: there is no
 * by-patient endpoint, and adding one would mean a schema change. The org read
 * already returns each booking's metadata, so the patient is present on every
 * row by the time it gets here.
 */
export async function readPatientBookings(
  accessToken: string,
  orgId: number,
  animalId: number
): Promise<VgApiResult<Booking[]>> {
  const { data, error } = await readOrgBookings(accessToken, orgId);
  if (error) return { data: null, error };

  const mine = (data ?? [])
    .filter((b) => b.animalId === animalId)
    .sort((a, b) => (a.start ?? "").localeCompare(b.start ?? ""));

  return { data: mine, error: null };
}
